import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useTexts } from "../hooks/useTexts";
import "./Auth.css";

export default function Account() {
    const { getText } = useTexts();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [courses, setCourses] = useState<any[]>([]);
    const [stats, setStats] = useState({
        courseProgress: 0,
        materialsRead: 0,
        streak: 7,
        points: 256
    });

    useEffect(() => {
        if (!user) return;

        const loadCourses = async () => {
            const q = query(
                collection(db, "courses"),
                where("teacherId", "==", user.uid)
            );
            const snap = await getDocs(q);
            const courseData = snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                createdAt: d.data().createdAt?.toDate() || new Date(),
            }));
            setCourses(courseData.slice(0, 4));
        };
        loadCourses();

        const timer = setTimeout(() => {
            setStats({
                courseProgress: 75,
                materialsRead: 90,
                streak: 7,
                points: 256
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [user]);

    const handleLogout = async () => {
        await signOut(auth);
        navigate("/");
    };

    if (!user) {
        return (
            <div className="auth-wrapper">
                <div className="auth-background"></div>
                <div className="auth-container" style={{ maxWidth: "480px" }}>
                    <div className="auth-glass auth-card not-authenticated">
                        <div className="auth-icon">
                            <div className="icon-wrapper">
                                <div className="icon-glow"></div>
                                <svg className="lock-icon" viewBox="0 0 24 24">
                                    <path d="M12 1C8.676 1 6 3.676 6 7v2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V11c0-1.1-.9-2-2-2h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v2H8V7c0-2.276 1.724-4 4-4zm6 4v4H6V6c0-3.3 2.7-6 6-6s6 2.7 6 6z"/>
                                </svg>
                            </div>
                        </div>
                        <h2>{getText("account_not_logged_title", "Zugriff erforderlich")}</h2>
                        <p>{getText("account_not_logged_text", "Bitte melden Sie sich an, um auf Ihr persönliches Dashboard zuzugreifen.")}</p>
                        <div className="feature-list">
                            <div className="feature-item">
                                <svg className="feature-icon" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>
                                <div><h4>{getText("account_feature_stats", "Persönliche Statistiken")}</h4><p>{getText("account_feature_stats_desc", "Verfolgen Sie Ihren Lernfortschritt")}</p></div>
                            </div>
                            <div className="feature-item">
                                <svg className="feature-icon" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/></svg>
                                <div><h4>{getText("account_feature_courses", "Kursverwaltung")}</h4><p>{getText("account_feature_courses_desc", "Verwalten Sie Ihre aktiven Kurse")}</p></div>
                            </div>
                        </div>
                        <div className="account-actions">
                            <button className="account-button primary" onClick={() => navigate("/login")}>
                                <span className="button-content">
                                    <span>{getText("account_login_btn", "Zum Login")}</span>
                                    <svg className="button-arrow" viewBox="0 0 24 24"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
                                </span>
                            </button>
                            <button className="account-button secondary" onClick={() => navigate("/register")}>
                                <span>{getText("account_register_btn", "Account erstellen")}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const initials = user.email?.split("@")[0].slice(0, 2).toUpperCase() || "U";
    const joinedDate = new Date(user.metadata.creationTime || "").toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric"
    });
    const lastLogin = new Date(user.metadata.lastSignInTime || "").toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });

    const level = Math.floor(stats.points / 100) + 1;
    const levelProgress = ((stats.points % 100) / 100) * 100;

    const calculateActivity = (createdAt: Date) => {
        const now = new Date();
        const diffDays = Math.ceil(Math.abs(now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays <= 7) return getText("account_activity_high", "Hoch");
        if (diffDays <= 30) return getText("account_activity_medium", "Mittel");
        return getText("account_activity_low", "Niedrig");
    };

    const getActivityColor = (activity: string) => {
        if (activity === getText("account_activity_high", "Hoch")) return "#22c55e";
        if (activity === getText("account_activity_medium", "Mittel")) return "#f59e0b";
        return "#ef4444";
    };

    return (
        <div className="auth-wrapper">
            <div className="auth-background"></div>
            <div className="auth-container" style={{ maxWidth: "1200px" }}>
                <div className="account-header-section">
                    <div className="account-header-card auth-glass">
                        <div className="header-content">
                            <div className="avatar-section">
                                <div className="avatar-container"><div className="avatar">{initials}</div></div>
                                <div className="level-indicator">
                                    <span className="level-label">{getText("account_level", "Level")} {level}</span>
                                    <div className="level-bar"><div className="level-progress" style={{ width: `${levelProgress}%` }}></div></div>
                                </div>
                            </div>
                            <div className="header-info">
                                <h1 className="user-greeting">{getText("account_welcome_back", "Willkommen zurück")}</h1>
                                <p className="user-email">{user.email}</p>
                                <div className="quick-stats">
                                    <div className="stat-card"><div className="stat-content"><span className="stat-value">{stats.courseProgress}%</span><span className="stat-label">{getText("account_stat_progress", "Kursfortschritt")}</span></div></div>
                                    <div className="stat-card"><div className="stat-content"><span className="stat-value">{stats.streak}</span><span className="stat-label">{getText("account_stat_streak", "Aktive Tage")}</span></div></div>
                                    <div className="stat-card"><div className="stat-content"><span className="stat-value">{courses.length}</span><span className="stat-label">{getText("account_stat_courses", "Meine Kurse")}</span></div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dashboard-grid">
                    <div className="dashboard-card auth-glass">
                        <div className="card-header"><h3><svg className="card-icon" viewBox="0 0 24 24"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>{getText("account_progress_title", "Fortschritt")}</h3></div>
                        <div className="card-content">
                            <div className="progress-item"><div className="progress-header"><span className="progress-label">{getText("account_progress_course", "Kursabschluss")}</span><span className="progress-value">{stats.courseProgress}%</span></div><div className="progress-track"><div className="progress-fill" style={{ width: `${stats.courseProgress}%` }}></div></div></div>
                            <div className="progress-item"><div className="progress-header"><span className="progress-label">{getText("account_progress_materials", "Materialien gelesen")}</span><span className="progress-value">{stats.materialsRead}%</span></div><div className="progress-track"><div className="progress-fill" style={{ width: `${stats.materialsRead}%` }}></div></div></div>
                            <div className="progress-item"><div className="progress-header"><span className="progress-label">{getText("account_progress_quiz", "Quiz Erfolgsrate")}</span><span className="progress-value">84%</span></div><div className="progress-track"><div className="progress-fill" style={{ width: '84%' }}></div></div></div>
                        </div>
                    </div>

                    <div className="dashboard-card auth-glass">
                        <div className="card-header"><h3><svg className="card-icon" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>{getText("account_details_title", "Account Details")}</h3><div className="status-indicator active">{getText("account_status_active", "Aktiv")}</div></div>
                        <div className="card-content">
                            <div className="detail-row"><span className="detail-label">{getText("account_details_registered", "Registriert am")}</span><span className="detail-value">{joinedDate}</span></div>
                            <div className="detail-row"><span className="detail-label">{getText("account_details_lastlogin", "Letztes Login")}</span><span className="detail-value">{lastLogin}</span></div>
                            <div className="detail-row"><span className="detail-label">{getText("account_details_userid", "User ID")}</span><span className="detail-value mono">{user.uid.slice(0, 12)}...</span></div>
                            <div className="detail-row"><span className="detail-label">{getText("account_details_status", "Status")}</span><span className="detail-value premium">{getText("account_status_premium", "Premium")}</span></div>
                        </div>
                    </div>

                    <div className="dashboard-card auth-glass">
                        <div className="card-header"><h3><svg className="card-icon" viewBox="0 0 24 24"><path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H8V4h12v12z"/></svg>{getText("account_courses_title", "Meine Kurse")} ({courses.length})</h3><button className="view-all" onClick={() => navigate("/courses")}>{getText("account_courses_viewall", "Alle ansehen")}</button></div>
                        <div className="card-content">
                            {courses.length === 0 ? (
                                <div className="empty-courses">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    <p>{getText("account_courses_empty", "Noch keine Kurse vorhanden")}</p>
                                    <button className="secondary-btn small" onClick={() => navigate("/courses/new")}>{getText("account_courses_create_first", "Ersten Kurs erstellen")}</button>
                                </div>
                            ) : (
                                <div className="course-list">
                                    {courses.map((course) => {
                                        const activity = calculateActivity(course.createdAt);
                                        const activityColor = getActivityColor(activity);
                                        return (
                                            <div key={course.id} className="course-item" onClick={() => navigate(`/courses/${course.id}`)}>
                                                <div className="course-info">
                                                    <div className="course-icon">{course.title?.charAt(0) || "K"}</div>
                                                    <div>
                                                        <div className="course-title">{course.title || getText("account_course_unnamed", "Unbenannter Kurs")}</div>
                                                        <div className="course-progress"><div className="course-track"><div className="course-fill" style={{ width: '60%', backgroundColor: activityColor }}></div></div><span style={{ color: activityColor }}>{activity} {getText("account_course_active", "Aktiv")}</span></div>
                                                    </div>
                                                </div>
                                                <button className="continue-btn" onClick={(e) => { e.stopPropagation(); navigate(`/courses/${course.id}`); }}><svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="dashboard-card auth-glass">
                        <div className="card-header"><h3><svg className="card-icon" viewBox="0 0 24 24"><path d="M12 2L8 8l-6 1 4 4-1 6 6-3 6 3-1-6 4-4-6-1z"/></svg>{getText("account_achievements_title", "Erfolge")}</h3><span className="count-badge">8/12</span></div>
                        <div className="card-content">
                            <div className="achievements-grid">
                                {[
                                    { name: getText("achievement_first_steps", "Erste Schritte"), earned: true },
                                    { name: getText("achievement_chat_expert", "Chat Expert"), earned: true },
                                    { name: getText("achievement_course_master", "Kurs Master"), earned: true },
                                    { name: getText("achievement_speed_learner", "Speed Learner"), earned: true },
                                    { name: getText("achievement_community_hero", "Community Hero"), earned: false },
                                    { name: getText("achievement_100_points", "100 Punkte"), earned: true }
                                ].map((achievement, index) => (
                                    <div key={index} className={`achievement ${achievement.earned ? 'earned' : 'locked'}`}>
                                        <div className="achievement-icon">
                                            {achievement.earned ? <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> : <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>}
                                        </div>
                                        <span className="achievement-name">{achievement.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="action-bar">
                    <button className="action-btn primary" onClick={() => navigate("/dashboard")}><svg className="action-icon" viewBox="0 0 24 24"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>{getText("account_action_dashboard", "Dashboard")}</button>
                    <button className="action-btn secondary" onClick={() => navigate("/courses")}><svg className="action-icon" viewBox="0 0 24 24"><path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z"/></svg>{getText("account_action_mycourses", "Meine Kurse")}</button>
                    <button className="action-btn tertiary" onClick={() => navigate("/chatbot")}><svg className="action-icon" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/></svg>{getText("account_action_chatbot", "AI Chatbot")}</button>
                    <button className="action-btn danger" onClick={handleLogout}><svg className="action-icon" viewBox="0 0 24 24"><path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>{getText("account_action_logout", "Ausloggen")}</button>
                </div>
            </div>
        </div>
    );
}